import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { supabase } from '@/client/lib/supabase';
import { trpc } from '@/client/utils/trpc';
import { trpcVanilla } from '@/client/utils/trpc-client';

export const Route = createFileRoute('/onboarding')({
  beforeLoad: async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw redirect({ to: '/login' });

    const { needsOnboarding } = await trpcVanilla.user.needsOnboarding.query();
    if (!needsOnboarding) throw redirect({ to: '/' });
  },
  component: OnboardingPage,
});

function OnboardingPage() {
  const router = useRouter();
  const { data: adminCheck } = trpc.user.hasAdminPassword.useQuery();
  const [fullName, setFullName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const complete = trpc.user.completeOnboarding.useMutation({
    onSuccess: () => router.navigate({ to: '/' }),
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError('');

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    const { error: pwError } = await supabase.auth.updateUser({ password });
    if (pwError) {
      setPasswordError(pwError.message);
      return;
    }

    complete.mutate({
      full_name: fullName,
      job_title: jobTitle.trim() || undefined,
      adminPassword: adminPassword || undefined,
    });
  }

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      {/* Brand panel */}
      <div
        className="relative hidden flex-[1.1] flex-col overflow-hidden p-11 text-white lg:flex"
        style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 60%, #2563eb 200%)' }}
      >
        <svg className="absolute inset-0 h-full w-full opacity-[0.06]" aria-hidden>
          <defs>
            <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M32 0H0V32" fill="none" stroke="#fff" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
        <div
          className="absolute -right-32 -top-28 h-[420px] w-[420px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.45), transparent 60%)' }}
          aria-hidden
        />
        <div className="relative">
          <div className="flex items-center gap-2">
            <svg width="26" height="26" viewBox="0 0 28 28" fill="none" aria-hidden>
              <rect x="0.5" y="0.5" width="27" height="27" rx="7" fill="#fafafa" />
              <path
                d="M19.5 11.2c-.7-1.7-2.4-2.9-4.5-2.9-2.8 0-5 2.2-5 5s2.2 5 5 5c2.4 0 4.4-1.7 4.9-4h-4.5"
                stroke="#0f172a"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-[18px] font-semibold tracking-tight text-white">
              Gara<span style={{ opacity: 0.55, fontWeight: 500, marginLeft: 2 }}>Tech</span>
            </span>
          </div>
        </div>
        <div className="relative mt-auto flex flex-col gap-5">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-white/55">
            Welcome
          </p>
          <h1 className="m-0 max-w-[480px] text-[44px] font-semibold leading-[1.05] tracking-tight">
            Let's get you set up.
          </h1>
          <p className="m-0 max-w-[420px] text-[15px] leading-relaxed text-white/70">
            Just a few details and you'll be ready to go.
          </p>
        </div>
        <div className="relative mt-8 text-xs text-white/45">© 2026 Gara Tech — Paris · Lyon</div>
      </div>

      {/* Form panel */}
      <div className="flex flex-1 items-center justify-center p-10">
        <div className="flex w-full max-w-[360px] flex-col gap-[22px]">
          <div className="flex flex-col gap-1.5">
            <h1 className="m-0 text-[26px] font-semibold tracking-tight text-slate-900">
              Your profile
            </h1>
            <p className="m-0 text-[13px] text-neutral-500">
              Tell us a bit about yourself to continue.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="full-name" className="text-[13px] font-medium text-slate-800">
                Full name <span className="text-red-500">*</span>
              </label>
              <input
                id="full-name"
                type="text"
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="Léa Dupont"
                className="h-[42px] w-full rounded-lg border border-stone-200 bg-white px-3 text-[14px] text-slate-900 placeholder:text-neutral-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="job-title" className="text-[13px] font-medium text-slate-800">
                Job title{' '}
                <span className="font-normal text-neutral-400">(optional)</span>
              </label>
              <input
                id="job-title"
                type="text"
                autoComplete="organization-title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Designer"
                className="h-[42px] w-full rounded-lg border border-stone-200 bg-white px-3 text-[14px] text-slate-900 placeholder:text-neutral-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-[13px] font-medium text-slate-800">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="h-[42px] w-full rounded-lg border border-stone-200 bg-white px-3 text-[14px] text-slate-900 placeholder:text-neutral-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="confirm-password" className="text-[13px] font-medium text-slate-800">
                Confirm password <span className="text-red-500">*</span>
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="h-[42px] w-full rounded-lg border border-stone-200 bg-white px-3 text-[14px] text-slate-900 placeholder:text-neutral-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            {passwordError && (
              <p className="text-[13px] text-red-600" role="alert">
                {passwordError}
              </p>
            )}

            {adminCheck?.enabled && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="admin-password" className="text-[13px] font-medium text-slate-800">
                  Admin password{' '}
                  <span className="font-normal text-neutral-400">(optional)</span>
                </label>
                <input
                  id="admin-password"
                  type="password"
                  autoComplete="off"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-[42px] w-full rounded-lg border border-stone-200 bg-white px-3 text-[14px] text-slate-900 placeholder:text-neutral-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
            )}

            {complete.error && (
              <p className="text-[13px] text-red-600" role="alert">
                {complete.error.message}
              </p>
            )}

            <button
              type="submit"
              disabled={complete.isPending}
              className="mt-0.5 flex h-[42px] w-full cursor-pointer items-center justify-center rounded-lg bg-slate-900 text-[15px] font-medium text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
            >
              {complete.isPending ? 'Saving…' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
